import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BarChart3, ShieldCheck, Target, Activity, Map, GraduationCap, Scale, ChevronRight, Loader2, MailCheck } from "lucide-react";
import { CaseStudiesHomeSection } from "./CaseStudies";
import { api, formatApiErrorDetail } from "../lib/api";

const services = [
  { icon: Target, title: "Program Evaluation", desc: "Full evaluation cycles for public health outcomes." },
  { icon: BarChart3, title: "Outcomes Measurement", desc: "Pre/post analysis, KPIs, and impact metrics." },
  { icon: Activity, title: "Logic Models", desc: "Theory of change and evaluation frameworks." },
  { icon: ShieldCheck, title: "Data Analysis", desc: "Epi analysis, trends, and interactive dashboards." },
  { icon: Map, title: "Community Health Assessments", desc: "CHNAs and ZIP-code health profiles." },
  { icon: GraduationCap, title: "Grant Evaluation Plans", desc: "Required for federal and state grant submissions." },
  { icon: Scale, title: "Health Equity Evaluation", desc: "Disparities, SDOH, ACEs, and access analysis." },
];

const stats = [
  { value: "$48M", label: "Grant Funding Supported" },
  { value: "30+", label: "Counties Served" },
];

const STATS_IMG = "https://images.unsplash.com/photo-1631556759511-6ce895fbf0ad?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState("");
  const [leadError, setLeadError] = useState("");

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    setLeadError("");
    setLeadSuccess("");
    try {
      const { data } = await api.post("/subscribe-lead", { email: leadEmail, source: "home-lead-block" });
      setLeadSuccess(
        data.already_subscribed
          ? "You're already on the list. If we still owe you a welcome email, we'll send it shortly."
          : "Thanks — check your inbox for your welcome email and 1 grant scorer unit offer."
      );
      setLeadEmail("");
    } catch (e) {
      setLeadError(formatApiErrorDetail(e.response?.data?.detail) || "Could not subscribe right now. Please try again.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 hero-grid-bg opacity-60" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/51954d6c-b726-4ca4-9496-440de1fb1560/images/c1b815335aa07c23916195270fe74aac2ddefb8d8fc57fe8a466e38abe2c7b5f.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-32 lg:pt-40 lg:pb-44">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline mb-6 text-[var(--silver)]" data-testid="hero-eyebrow">Nevada-Based Evaluation Firm</div>
            <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
              Measure what <span className="text-[var(--red)]">matters.</span><br />
              Improve what <span className="text-[var(--silver)]">works.</span>
            </h1>
            <p className="mt-7 text-lg text-[var(--text-2)] max-w-2xl leading-relaxed">
              We help agencies, nonprofits, and health systems quantify outcomes, design rigorous evaluations, and make decisions grounded in evidence — not assumption.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-ctas">
              <Link to="/book-consultation" className="btn-red" data-testid="hero-book-btn">
                Book a Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="btn-ghost" data-testid="hero-services-btn">
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-[var(--border)] bg-black/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-3 divide-x divide-[var(--border)]">
            <div className="py-4 px-2 lg:px-6 flex items-center" data-testid="stat-team-image">
              <img
                src={STATS_IMG}
                alt="PHMEG evaluation team reviewing data"
                className="w-full h-20 lg:h-24 object-cover object-center border border-[var(--border)]"
              />
            </div>
            {stats.map((s) => (
              <div key={s.label} className="py-8 px-2 lg:px-6" data-testid={`stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="font-display text-3xl lg:text-4xl font-semibold text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--text-3)] mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="home-services">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="overline text-[var(--red)] mb-3">What we do</div>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight max-w-2xl">
              Research-grade evaluation services for public health programs.
            </h2>
          </div>
          <Link to="/services" className="text-[var(--silver)] hover:text-white text-sm inline-flex items-center gap-2 group">
            All services <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
          {services.map((s) => (
            <div key={s.title} className="bg-[var(--bg)] p-8 hover:bg-[var(--surface)] transition-colors group" data-testid={`service-${s.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <s.icon className="w-7 h-7 text-[var(--red)] mb-6" />
              <h3 className="font-display text-xl font-semibold mb-2 tracking-tight">{s.title}</h3>
              <p className="text-sm text-[var(--text-2)] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IMPACT / SHOWCASE */}
      <section className="border-y border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="overline text-[var(--silver)] mb-4">Evidence in motion</div>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight leading-tight">
              Dashboards your stakeholders <span className="text-[var(--red)]">actually use.</span>
            </h2>
            <p className="mt-6 text-[var(--text-2)] leading-relaxed">
              We move beyond static PDFs. Our deliverables include interactive dashboards, clean data pipelines, and visualization frameworks calibrated to your funders&apos; reporting requirements.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[var(--text-2)]">
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-[var(--red)] mt-2" /> Federal & state funder-aligned reporting (CDC, HRSA, SAMHSA)</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-[var(--red)] mt-2" /> Equity-centered indicators and SDOH integration</li>
              <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-[var(--red)] mt-2" /> Mixed-methods rigor with rapid-cycle insights</li>
            </ul>
          </div>
          <div className="relative">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/51954d6c-b726-4ca4-9496-440de1fb1560/images/7e56653388751f454ae296f8733f4409afaa911c1a90f1eda35072ec3e5d110f.png"
              alt="Dashboard visualization"
              className="w-full max-h-[80vh] object-cover border border-[var(--border)] red-glow"
            />
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <CaseStudiesHomeSection />

      {/* LEAD CAPTURE */}
      <section className="border-y border-[var(--border)] bg-[#1a1a1a]" data-testid="lead-capture-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <div className="overline text-[var(--silver)] mb-4" data-testid="lead-capture-eyebrow">Member preview offer</div>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight leading-tight" data-testid="lead-capture-title">
              Get <span className="text-[var(--red)]">1 grant scorer unit</span> when you join our email list.
            </h2>
            <p className="mt-6 text-[var(--text-2)] leading-relaxed max-w-2xl" data-testid="lead-capture-copy">
              Subscribe for PHMEG updates, funding insights, and a welcome email confirming your 1 grant scorer unit offer.
            </p>
          </div>

          <form onSubmit={handleLeadSubmit} className="card-dark p-8 space-y-4" data-testid="lead-capture-form">
            <label className="overline block" htmlFor="lead-email-input">Email address</label>
            <input
              id="lead-email-input"
              type="email"
              required
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              className="input-dark"
              placeholder="you@organization.org"
              data-testid="lead-capture-email-input"
            />
            <p className="text-sm text-[var(--text-3)]" data-testid="lead-capture-helper">
              We’ll only use your email for PHMEG follow-up and relevant updates.
            </p>
            <p className="text-xs text-[var(--text-3)] leading-relaxed" data-testid="lead-capture-placeholder-note">
              Placeholder note: the public free-unit upload-and-email claim flow will be enabled only after final verification.
            </p>
            {leadError && <div className="text-sm text-[var(--red)]" data-testid="lead-capture-error">{leadError}</div>}
            {leadSuccess && (
              <div className="flex items-start gap-2 text-sm text-emerald-400" data-testid="lead-capture-success">
                <MailCheck className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{leadSuccess}</span>
              </div>
            )}
            <button type="submit" disabled={leadSubmitting} className="btn-red w-full justify-center" data-testid="lead-capture-submit-button">
              {leadSubmitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Joining…</>) : "Claim your welcome email"}
            </button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28 text-center">
        <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight">
          Ready to make your impact <span className="text-[var(--red)]">measurable</span>?
        </h2>
        <p className="mt-5 text-[var(--text-2)] max-w-2xl mx-auto">
          Start with a free 30-minute consultation. We&apos;ll discuss your program, your data, and the path to credible outcomes.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/book-consultation" className="btn-red" data-testid="cta-book-btn">Book Free Consultation</Link>
          <Link to="/contact" className="btn-ghost" data-testid="cta-contact-btn">Send a Message</Link>
        </div>
      </section>
    </div>
  );
}
